/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import React, { createContext, forwardRef, useId } from "react";

import { spacing } from "../../../../../global/emotion";
import { FormControl } from "./FormControl.emotion";
import { FormDescription } from "./FormDescription.emotion";
import { useFormField } from "./FormField.emotion";
import type { FormMessageProps } from "./FormMessage.emotion";
import { FormMessage } from "./FormMessage.emotion";

const formItemStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

type FormItemContextValue = {
  id: string;
};

export const FormItemContext = createContext<FormItemContextValue>(
  {} as FormItemContextValue,
);

export type FormItemProps = React.HTMLAttributes<HTMLDivElement> &
  Pick<FormMessageProps, "showErrorMessage" | "showWarningMessage"> & {
    description?: React.ReactNode;
  };

export const FormItem = forwardRef<HTMLDivElement, FormItemProps>(
  (
    {
      children,
      showErrorMessage = true,
      showWarningMessage = true,
      description,
      ...props
    },
    ref,
  ) => {
    const id = useId();
    const { error, formMessageId } = useFormField();

    return (
      <FormItemContext.Provider value={{ id }}>
        <div ref={ref} css={formItemStyle} {...props}>
          <FormControl>{children}</FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          {
            <FormMessage
              showWarningMessage={showWarningMessage}
              showErrorMessage={showErrorMessage}
            />
          }
        </div>
      </FormItemContext.Provider>
    );
  },
);

FormItem.displayName = "FormItem";
